import { z } from 'zod';
import type { AiUsageLedgerDimensionsInput } from './ai-usage';

const safeInteger = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const mediaSource = z.string().trim().min(1).max(2_000_000);
const mediaQuality = z.enum(['auto', 'low', 'medium', 'high']);
const mediaResolution = z.enum(['auto', 'low', 'medium', 'high']);

/**
 * 传给模型的媒体引用只描述已授权的外部媒体地址；媒体存储和下载仍由
 * 既有媒体链路负责。计量字段属于请求合同，不把 provider 原始响应写入账本。
 */
export const AiImageAttachmentSchema = z
  .object({
    type: z.literal('image'),
    url: mediaSource,
    width: safeInteger.min(1).max(100_000).optional(),
    height: safeInteger.min(1).max(100_000).optional(),
    resolution: mediaResolution.optional(),
    quality: mediaQuality.optional(),
  })
  .strict()
  .refine((value) => (value.width === undefined) === (value.height === undefined), {
    message: 'image width and height must be provided together',
    path: ['width'],
  });

export const AiAudioAttachmentSchema = z
  .object({
    type: z.literal('audio'),
    url: mediaSource,
    duration_ms: safeInteger.min(1).max(86_400_000),
    format: z.enum(['wav', 'mp3']).optional(),
  })
  .strict();

export const AiMediaAttachmentSchema = z.discriminatedUnion('type', [
  AiImageAttachmentSchema,
  AiAudioAttachmentSchema,
]);

export type AiImageAttachment = z.output<typeof AiImageAttachmentSchema>;
export type AiAudioAttachment = z.output<typeof AiAudioAttachmentSchema>;
export type AiMediaAttachment = z.output<typeof AiMediaAttachmentSchema>;

export type AiMediaUsage = Pick<
  AiUsageLedgerDimensionsInput,
  'image_input_units' | 'image_output_units' | 'audio_input_units' | 'audio_output_units'
>;

const QUALITY_UNITS: Record<z.infer<typeof mediaQuality>, number> = {
  auto: 1,
  low: 1,
  medium: 2,
  high: 3,
};

const RESOLUTION_UNITS: Record<z.infer<typeof mediaResolution>, number> = {
  auto: 1,
  low: 1,
  medium: 2,
  high: 4,
};

/**
 * 将请求媒体转换为可发布计费策略消费的用量单位。
 *
 * 图片按百万像素桶和质量因子计量；缺少真实尺寸时使用请求声明的
 * resolution 桶。音频按完整秒向上取整。这里的常量是计量单位定义，
 * 不是商业价格；实际扣点仍只读取已发布 billing policy 的 rates。
 */
export function estimateAiMediaUsage(attachments: readonly AiMediaAttachment[]): AiMediaUsage {
  let imageInputUnits = 0;
  let audioInputUnits = 0;
  for (const attachment of attachments) {
    if (attachment.type === 'image') {
      const resolutionUnits =
        attachment.width !== undefined && attachment.height !== undefined
          ? Math.max(1, Math.ceil((attachment.width * attachment.height) / 1_000_000))
          : RESOLUTION_UNITS[attachment.resolution ?? 'auto'];
      imageInputUnits = addMediaUnits(
        imageInputUnits,
        resolutionUnits * QUALITY_UNITS[attachment.quality ?? 'auto'],
      );
    } else {
      audioInputUnits = addMediaUnits(audioInputUnits, Math.ceil(attachment.duration_ms / 1_000));
    }
  }
  return {
    image_input_units: imageInputUnits,
    image_output_units: 0,
    audio_input_units: audioInputUnits,
    audio_output_units: 0,
  };
}

export function mergeAiMediaUsage(
  first: Partial<AiMediaUsage> | null | undefined,
  second: Partial<AiMediaUsage> | null | undefined,
): AiMediaUsage {
  return {
    image_input_units: addMediaUnits(first?.image_input_units ?? 0, second?.image_input_units ?? 0),
    image_output_units: addMediaUnits(
      first?.image_output_units ?? 0,
      second?.image_output_units ?? 0,
    ),
    audio_input_units: addMediaUnits(first?.audio_input_units ?? 0, second?.audio_input_units ?? 0),
    audio_output_units: addMediaUnits(
      first?.audio_output_units ?? 0,
      second?.audio_output_units ?? 0,
    ),
  };
}

function addMediaUnits(first: number, second: number): number {
  const total = first + second;
  if (!Number.isSafeInteger(total)) throw new Error('AI media usage is too large');
  return total;
}
